import { useCallback, useEffect, useState } from 'react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

// Stripe events: whether Stripe can reach us, when it last did, and any
// event we stored but failed to act on - each with a Retry button. A
// failed event is never left to Stripe's retry schedule (it was answered
// 200); this page is the recovery path.

interface FailedEvent {
  id: string;
  type: string;
  receivedAt: string;
  attempts: number;
  lastError: string | null;
}

interface StripeEventsView {
  webhookConfigured: boolean;
  lastReceivedAt: string | null;
  failed: FailedEvent[];
}

const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });

export function StripeEventsPage(): React.ReactElement {
  useDocumentTitle('Stripe events');
  const [view, setView] = useState<StripeEventsView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await apiFetch<StripeEventsView>('/api/billing/stripe-events'));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load Stripe events.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-[var(--color-header)]">Stripe events</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          What Stripe tells us about payments we did not start ourselves: a monthly charge that failed, an invoice
          the client paid from its link, a deposit with no signature. Anything we could not act on is listed here
          with a Retry button.
        </p>
      </header>

      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
      {!view && !error && <p className="text-sm text-[var(--color-muted)]">Loading…</p>}

      {view && (
        <Card>
          {view.webhookConfigured ? (
            <p className="text-sm text-[var(--color-body)]">
              The webhook is set up.{' '}
              {view.lastReceivedAt
                ? `Last event received ${when(view.lastReceivedAt)}.`
                : 'No event has been received yet.'}
            </p>
          ) : (
            <p className="rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              The webhook is not set up, so Stripe cannot tell us about payments. In Stripe → Developers → Webhooks,
              add the endpoint <code>https://portal.t3techpartners.com/api/webhooks/stripe</code>, then set its
              signing secret as <code>STRIPE_WEBHOOK_SECRET</code> in App Platform. Until then, the &ldquo;Check if
              the client paid&rdquo; buttons still work.
            </p>
          )}
        </Card>
      )}

      {view && view.failed.length === 0 && (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">Nothing failed. Every event Stripe sent has been handled.</p>
        </Card>
      )}

      {view?.failed.map((ev) => <FailedRow key={ev.id} event={ev} onView={setView} />)}

      <OtherModeReferences />
    </div>
  );
}

interface StripeRef {
  kind: string;
  stripeId: string;
  label: string;
  state: 'ok' | 'other_mode' | 'missing' | 'error';
  clearable: boolean;
  detail: string | null;
}

interface StripeRefScan {
  checked: number;
  otherMode: number;
  clearable: number;
  refs: StripeRef[];
}

const KIND_LABEL: Record<string, string> = {
  customer: 'Customer',
  subscription: 'Subscription',
  card: 'Saved card',
  first_month_invoice: 'First-month invoice',
  balance_invoice: 'Balance invoice',
  overage_invoice: 'Overage invoice',
  deposit: 'Deposit (signature record)',
  card_setup: 'Card at signing (signature record)',
};

const STATE_LABEL: Record<StripeRef['state'], string> = {
  ok: 'OK',
  other_mode: 'Other mode',
  missing: 'Not found',
  error: 'Could not check',
};

/**
 * After moving from test keys to live: the Stripe ids saved while testing
 * point at nothing in the new mode. Check asks Stripe about each one;
 * Clear forgets the ones Stripe says are the other mode's, in one go.
 */
function OtherModeReferences(): React.ReactElement {
  const [scan, setScan] = useState<StripeRefScan | null>(null);
  const [busy, setBusy] = useState<'check' | 'clear' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cleared, setCleared] = useState<string | null>(null);

  async function check(): Promise<void> {
    setBusy('check');
    setError(null);
    setCleared(null);
    try {
      setScan(await apiFetch<StripeRefScan>('/api/billing/stripe-references'));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The check did not run.');
    } finally {
      setBusy(null);
    }
  }

  async function clear(): Promise<void> {
    if (!scan) return;
    const ok = window.confirm(
      `Clear ${scan.clearable} Stripe ${scan.clearable === 1 ? 'id' : 'ids'} from the other mode?\n\n` +
        'Clients lose the test customer and card billing set up while testing. Block-hours clients will need a new "Set up card billing" link. Deposits and paid invoices are not touched.',
    );
    if (!ok) return;
    setBusy('clear');
    setError(null);
    try {
      const r = await apiFetch<{
        customers: number;
        subscriptions: number;
        balanceInvoices: number;
        overageInvoices: number;
        scan: StripeRefScan;
      }>('/api/billing/stripe-references/clear', { method: 'POST', json: {} });
      setScan(r.scan);
      setCleared(
        `Cleared ${r.customers} ${r.customers === 1 ? 'customer' : 'customers'}, ${r.subscriptions} block-hours card ${
          r.subscriptions === 1 ? 'setup' : 'setups'
        }, ${r.balanceInvoices + r.overageInvoices} unpaid ${r.balanceInvoices + r.overageInvoices === 1 ? 'invoice' : 'invoices'}.`,
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nothing was cleared.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h2 className="text-base font-semibold text-[var(--color-header)]">Stripe ids from the other mode</h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            After switching Stripe keys between test and live, the customers, cards and subscriptions saved under the old
            keys no longer exist, and the first charge for those clients would fail. Check asks Stripe about every id the
            app holds. Clear forgets the ones Stripe says belong to the other mode, so the next charge starts fresh.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void check()} disabled={busy !== null}>
            {busy === 'check' ? 'Checking…' : 'Check'}
          </Button>
          {scan && scan.clearable > 0 && (
            <Button onClick={() => void clear()} disabled={busy !== null}>
              {busy === 'clear' ? 'Clearing…' : `Clear ${scan.clearable}`}
            </Button>
          )}
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}
      {cleared && <p className="mt-3 text-sm text-[var(--color-body)]">{cleared}</p>}

      {scan && (
        <div className="mt-4">
          <p className="text-sm text-[var(--color-body)]">
            {scan.checked === 0
              ? 'The app holds no Stripe ids.'
              : scan.otherMode === 0
                ? `Checked ${scan.checked} ${scan.checked === 1 ? 'id' : 'ids'}. None are from the other mode.`
                : `Checked ${scan.checked}. ${scan.otherMode} from the other mode, ${scan.clearable} of them clearable.`}
          </p>
          {scan.refs.some((r) => r.state !== 'ok') && (
            <table className="mt-3 w-full text-left text-sm">
              <thead className="text-xs uppercase text-[var(--color-muted)]">
                <tr>
                  <th className="py-1 pr-3">What</th>
                  <th className="py-1 pr-3">Whose</th>
                  <th className="py-1 pr-3">Stripe says</th>
                </tr>
              </thead>
              <tbody>
                {scan.refs
                  .filter((r) => r.state !== 'ok')
                  .map((r) => (
                    <tr key={`${r.kind}-${r.stripeId}`} className="border-t border-[var(--color-border)] align-top">
                      <td className="py-2 pr-3">
                        {KIND_LABEL[r.kind] ?? r.kind}
                        <span className="block text-xs text-[var(--color-muted)]">{r.stripeId}</span>
                      </td>
                      <td className="py-2 pr-3">{r.label}</td>
                      <td className="py-2 pr-3">
                        {STATE_LABEL[r.state]}
                        {r.state === 'other_mode' && !r.clearable && (
                          <span className="block text-xs text-[var(--color-muted)]">Kept: part of a signed or paid record.</span>
                        )}
                        {r.state !== 'other_mode' && r.detail && (
                          <span className="block text-xs text-[var(--color-muted)]">{r.detail}</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
}

function FailedRow({ event, onView }: { event: FailedEvent; onView: (v: StripeEventsView) => void }): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function retry(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; error: string | null; view: StripeEventsView }>(
        `/api/billing/stripe-events/${encodeURIComponent(event.id)}/retry`,
        { method: 'POST', json: {} },
      );
      if (!data.ok) setError(data.error ?? 'It failed again.');
      onView(data.view);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Retry did not run.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-[var(--color-header)]">{event.type}</p>
          <p className="text-sm text-[var(--color-muted)]">
            {event.id} · received {when(event.receivedAt)} · {event.attempts}{' '}
            {event.attempts === 1 ? 'attempt' : 'attempts'}
          </p>
        </div>
        <Button onClick={() => void retry()} disabled={busy}>
          {busy ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
      {(error ?? event.lastError) && (
        <p className="mt-3 rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {error ?? event.lastError}
        </p>
      )}
    </Card>
  );
}
